// models
import { makeStyles } from '@fluentui/react-components';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractBranch } from '../../../models/Entities/Contracts/ContractBranch';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { ProtocolPatientContractBranch } from '../../../models/Entities/Protocols/ProtocolPatientContractBranch';
import { ErrorType } from '../../../models/HttpError';

// stores
import * as ContractStore from '../../../redux/middleware/ContractMiddleware'
import * as ContractStageStore from '../../../redux/middleware/ContractStageMiddleware'
import * as ContractBranchStore from '../../../redux/middleware/ContractBranchsMiddleware'
import * as ProtocolsStore from '../../../redux/middleware/MedicalProtocolMiddleware'
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware'
import * as ProtocolPractitionersStore from '../../../redux/middleware/ProtocolPractitionersMiddleware'

// state
import { AppState } from '../../../redux/reducers';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { ProtocolVisit } from '../../../models/Entities/Protocols/ProtocolVisit';
import { HcProtocolPatient } from '../../../models/Entities/PractitionerAssociations/HcProtocolPatient';

//#region Props

interface IProps {
    ProtocolId: number;
}

interface ConnectedProps {
    IsLoadingProtocol: boolean;
    LoadedProtocolSuccessfully: boolean;
    ErrorLoadingProtocol: ErrorType | undefined;
    IsLoadingContract: boolean;
    LoadedContractSuccessfully: boolean;
    ErrorLoadingContract: ErrorType | undefined;
    ContractId: number | null;
    Protocol: Protocol | null;
    Contract: Contract | null;
    ContractBranches: ContractBranch[];
    IsLoadingContractBranches: boolean;
    ContractBranchesLoaded: boolean;
    ProtocolPatients: ProtocolPatient[];
    ProtocolPatientContractBranches: ProtocolPatientContractBranch[];
    Stages: ContractStage[];
    PatientVisits: PatientVisit[];
    Visits: ProtocolVisit[];
    GetPractitioner: (id: string) => Practitioner | undefined;
    Patients: string[];
    HcProtocolPatients: HcProtocolPatient[];
    IsLoadingHcProtocolPatients: boolean;
    IsGettingVisits: boolean;    
}

interface MedicalServicesDispatchProps {
    GetMedicalProtocolById: typeof ProtocolsStore.actionCreators.GetMedicalProtocolById;
    GetProtocolPatients: typeof ProtocolsStore.actionCreators.GetProtocolPatients;
    GetProtocolPatientContractBranches: typeof ProtocolsStore.actionCreators.GetProtocolPatientContractBranches;
    SetProtocolPatientContractBranch: typeof ProtocolsStore.actionCreators.SetProtocolPatientContractBranch;
    DeleteProtocolPatientContractBranch: typeof ProtocolsStore.actionCreators.DeleteProtocolPatientContractBranch;
    GetContractById: typeof ContractStore.actionCreators.GetContractById;
    GetAllContractsBranchs: typeof ContractBranchStore.actionCreators.GetAllContractsBranchs;
    GetAllContractsStage: typeof ContractStageStore.actionCreators.GetAllContractsStage;
    GetVisitsByProtocolId: typeof ProtocolsStore.actionCreators.GetVisitsByProtocolId;
    GetPatientVisitByProtocolId: typeof ProtocolsStore.actionCreators.GetPatientVisitByProtocolId;
    GetAllPractitioners: typeof PractitionerStore.actionCreators.GetAllPractitioners;
    GetAllProtocolContractAssociations: typeof ProtocolsStore.actionCreators.GetAllProtocolContractAssociations;
    GetHcProtocolPatientsByProtocolId: typeof ProtocolPractitionersStore.actionCreators.GetHcProtocolPatientsByProtocolId;
}

export type Props = IProps & ConnectedProps & MedicalServicesDispatchProps;

// mapStateToProps runs on every dispatched action of the whole app, and connect() compares the
// values it returns by identity. Anything rebuilt here (a new array, a new arrow function) makes
// this very large grid re-render on every action, which is what makes tooltips, popovers and
// callouts flicker or close on their own. So everything derived is memoized by its source.
const EMPTY_LIST: readonly never[] = [];

const emptyList = <TItem, >(): TItem[] => EMPTY_LIST as unknown as TItem[];

const memoizeBySource = <TSource, TResult>(compute: (source: TSource) => TResult) => {
    let lastSource: TSource | undefined;
    let lastResult: TResult | undefined;

    return (source: TSource): TResult => {
        if (source !== lastSource || lastResult === undefined) {
            lastSource = source;
            lastResult = compute(source);
        }

        return lastResult as TResult;
    };
};

// A patient number almost always carries a sequence ("P-2" vs "P-10"), and a plain localeCompare
// orders those alphabetically: 1, 10, 2. The comparison is numeric-aware and ignores case/accents.
export const ComparePatientNumbers = (a?: string, b?: string): number =>
    (a || '').localeCompare(b || '', undefined, { sensitivity: 'base', numeric: true });

const getSortedProtocolPatients = memoizeBySource((patients: ProtocolPatient[] | undefined) =>
    patients
        // a copy: sorting in place would mutate the store
        ? [...patients].sort((a: ProtocolPatient, b: ProtocolPatient) => ComparePatientNumbers(a.numberInProtocol, b.numberInProtocol))
        : emptyList<ProtocolPatient>()
);

const getPatientNumbers = memoizeBySource((patientVisits: PatientVisit[] | undefined) =>
    (patientVisits || []).map((visit: PatientVisit) => visit.numberInProtocol).reduce(
        (acc: string[], current: string) => {
            if (!acc.includes(current)) {
                acc.push(current);
            }
            return acc;
        }, [] as string[]
    ).sort(ComparePatientNumbers)
);

const getPractitionerLookup = memoizeBySource((practitioners: Practitioner[] | undefined) => {
    const practitionersByUserId = new Map<string, Practitioner>(
        (practitioners || []).map((practitioner: Practitioner) => [practitioner.userId, practitioner])
    );

    return (id: string) => practitionersByUserId.get(id);
});

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const AuxContractId: number | null =  state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId)?.contractAssociation?.contractId || null;

    const AuxContract: Contract | null = state.Contract?.list?.find((contract: Contract) => contract?.id === AuxContractId) || null;

    const AuxProtocol: Protocol | null = state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId) || null;

    const AuxPatients: string[] = getPatientNumbers(AuxProtocol?.patientVisits);

    var AuxContractStages: ContractStage[] = emptyList<ContractStage>();

    if(AuxContract) {
        AuxContractStages = state.Contract?.list?.find((contract: Contract) => contract?.id === AuxContractId)?.stages || emptyList<ContractStage>();
    }

    return {
        IsLoadingProtocol: state.MedicalProtocols?.isLoadingOne,
        LoadedProtocolSuccessfully: state.MedicalProtocols?.successLoadingOne,
        ErrorLoadingProtocol: state.MedicalProtocols?.error,
        IsLoadingContract: state.Contract?.isLoadingOne,
        LoadedContractSuccessfully: state.Contract?.successLoadingOne,
        ErrorLoadingContract: state.Contract?.error,
        Protocol: AuxProtocol || null,
        ContractId: AuxContractId,
        Contract: AuxContract,
        ContractBranches: state.ContractBranch?.list || emptyList<ContractBranch>(),
        IsLoadingContractBranches: state.ContractBranch?.isLoadingAll || false,
        ContractBranchesLoaded: state.ContractBranch?.successLoadingAll === true,
        ProtocolPatients: getSortedProtocolPatients(AuxProtocol?.patients),
        ProtocolPatientContractBranches: AuxProtocol?.patientContractBranches || emptyList<ProtocolPatientContractBranch>(),
        Stages: AuxContractStages,
        PatientVisits: AuxProtocol?.patientVisits || emptyList<PatientVisit>(),
        Visits: AuxProtocol?.visits || emptyList<ProtocolVisit>(),
        GetPractitioner: getPractitionerLookup(state.Practitioners?.list),
        Patients: AuxPatients,
        HcProtocolPatients: state.ProtocolPractitioners?.hcProtocolPatients || emptyList<HcProtocolPatient>(),
        IsLoadingHcProtocolPatients: state.ProtocolPractitioners?.isLoadingHcProtocolPatients || false,
        IsGettingVisits: AuxProtocol?.isGettingVisits || false
    }
};

export const mapDispatchToProps = {
    ...ContractStore.actionCreators,
    ...ContractBranchStore.actionCreators,
    ...ProtocolsStore.actionCreators,
    ...ContractStageStore.actionCreators,
    ...PractitionerStore.actionCreators,
    ...ProtocolPractitionersStore.actionCreators
};


// component functions

export interface internalStage {
    columnKey: string;
    stageId: number | null;
    branchId?: number | null;
    // set by the activity grid when branchId points at a contract branch that no longer exists
    isOrphanBranch?: boolean;
    orphanBranchId?: number;
    // performed visits outside the protocol flowchart (crossVersionId 0)
    isExtraProtocolVisit?: boolean;
    // records titled NOTE: annotations on the patient's timeline, not visits
    isNoteVisit?: boolean;
    // ids of the patient records this ad-hoc column holds, one per patient at most.
    // Shared by the extra-protocol and the note columns: both are placed by id.
    extraVisitIds?: Set<number>;
    stageName: string;
    flowchartName?: string;
    order: number;
    crossVersionId?: number;
    isRemote: boolean;
    remoteWarning: boolean;
}

// crossVersionId 0 (or missing) is not a flowchart version: those are the ExtraProtocolVisit ones
export const IsExtraProtocolVisit = (crossVersionId?: number | null): boolean => !crossVersionId;

const EXTRA_PROTOCOL_VISIT_TYPE = 'extraprotocolvisit';

// The API marks them explicitly ("type": "ExtraProtocolVisit", with crossVersionId 0); the id is
// only the fallback for payloads that carry the 0 without the type.
export const IsExtraProtocolPatientVisit = (visit: PatientVisit): boolean => {
    // Una NOTE también viene con crossVersionId 0, así que el descarte va primero:
    // si no, el fallback por id se las llevaría a la columna de visitas extra.
    if (IsNoteVisit(visit)) {
        return false;
    }

    return (visit.type || '').trim().toLowerCase() === EXTRA_PROTOCOL_VISIT_TYPE
        || (visit.title || '').trim().toLowerCase() === EXTRA_PROTOCOL_VISIT_TYPE
        || IsExtraProtocolVisit(visit.crossVersionId);
};

// those records carry the technical name as their title, which is no title to show on a column
const GetExtraVisitDisplayTitle = (visit: PatientVisit): string => {
    const title = (visit.title || '').trim();

    return title.toLowerCase() === EXTRA_PROTOCOL_VISIT_TYPE ? '' : title;
};

// A record titled NOTE is an annotation, not a visit: it takes no part in the activity grid nor in
// the summary. The title is what identifies it; type is checked too when it happens to come filled.
export const IsNoteVisit = (visit: PatientVisit): boolean => {
    const title = (visit.title || '').trim().toLowerCase();
    const type = (visit.type || '').trim().toLowerCase();

    return title === 'note' || type === 'note';
};

export const WithoutNoteVisits = (visits: PatientVisit[]): PatientVisit[] =>
    (visits || []).filter((visit: PatientVisit) => !IsNoteVisit(visit));

const NormalizeVisitTitle = (title?: string): string => (title || '').trim().toLowerCase();

const EXTRA_VISIT_ORDER_BASE = 1000000;

const NormalizePatientNumber = (patientNumberInProtocol?: string): string =>
    (patientNumberInProtocol || '').trim().toLowerCase();

// a performed visit belongs to a column through its flowchart version. Columns without one (stages
// that were never linked) must not swallow the extra-protocol visits, which are assigned per patient
// by BuildExtraProtocolVisitColumns and matched here by the visit id it was placed under.
/**
 * Una columna sin etapa que además no sea ad-hoc: eso, y sólo eso, es una
 * visita realizada que el contrato no define. Ni las visitas extra ni las notas
 * son anomalías, así que no llevan las alertas de fuera de contrato.
 */
export const IsOutOfContractStage = (stage: internalStage): boolean =>
    !stage.stageId && !stage.isExtraProtocolVisit && !stage.isNoteVisit;

export const MatchesStage = (visit: PatientVisit, stage: internalStage): boolean => {
    // Las columnas ad-hoc (visitas extra y notas) se resuelven por id, porque no
    // tienen versión de flowchart con la cual emparejar.
    if (stage.isExtraProtocolVisit || stage.isNoteVisit) {
        return !!stage.extraVisitIds?.has(visit.id);
    }

    // Una nota nunca cae en una columna del flowchart, ni siquiera si el
    // crossVersionId viniera cargado.
    if (IsNoteVisit(visit)) {
        return false;
    }

    return !!stage.crossVersionId && visit.crossVersionId === stage.crossVersionId;
}

const IsSamePatient = (visit: PatientVisit, patientNumberInProtocol: string): boolean =>
    NormalizePatientNumber(visit.numberInProtocol) === NormalizePatientNumber(patientNumberInProtocol);

const GetVisitTime = (visit: PatientVisit): number | undefined => {
    const time = visit.date ? new Date(visit.date).getTime() : NaN;

    return Number.isFinite(time) ? time : undefined;
};

// a stage can span several columns; an extra visit anchored to one of them goes after the last of
// them, so the stage's columns stay together
const GetLastColumnIndexOfSameStage = (columns: internalStage[], anchorIndex: number): number => {
    const anchorStageId = columns[anchorIndex]?.stageId;

    if (anchorStageId === null || anchorStageId === undefined) {
        return anchorIndex;
    }

    let lastIndex = anchorIndex;

    while (lastIndex + 1 < columns.length && columns[lastIndex + 1].stageId === anchorStageId) {
        lastIndex++;
    }

    return lastIndex;
};

// Extra-protocol visits are placed with each patient's own chronology: an extra visit sits right
// after the last flowchart column that THAT patient performed on or before it. Patients that share
// a slot share the column, and a patient with several extra visits in the same slot pushes as many
// columns as needed, always in date order.
/**
 * Cómo se arma una familia de columnas ad-hoc (visitas extra, notas). Todas
 * comparten el mismo problema: son registros que no están en el flowchart, hay
 * varios por paciente y tienen que caer en la fecha que les corresponde.
 */
interface AdHocColumnOptions {
    /** Qué registros arma esta familia. */
    matches: (visit: PatientVisit) => boolean;
    /** Prefijo de columnKey, para que las dos familias no colisionen. */
    keyPrefix: string;
    /** Título que aporta un registro, si aporta alguno. */
    displayTitle: (visit: PatientVisit) => string;
    /** Título cuando los registros de la columna no comparten nombre. */
    fallbackTitle: (slotIndex: number, slotsInAnchor: number) => string;
    /** Marcas que llevan las columnas resultantes. */
    flags: Pick<internalStage, 'isExtraProtocolVisit' | 'isNoteVisit'>;
    /**
     * Desempate de orden entre familias. Dos familias ancladas en la misma
     * columna y el mismo slot caerían en el mismo `order` y quedarían en un
     * orden indefinido.
     */
    orderOffset: number;
}

const BuildAdHocVisitColumns = (
    columns: internalStage[],
    patientVisits: PatientVisit[],
    options: AdHocColumnOptions
): internalStage[] => {
    const extraVisits = patientVisits.filter(options.matches);

    if (extraVisits.length === 0) {
        return [];
    }

    const patients = Array.from(new Set(extraVisits.map((visit: PatientVisit) => NormalizePatientNumber(visit.numberInProtocol))));
    // anchorIndex: -1 before the first column, columns.length for the ones with no date
    const slots = new Map<string, { anchorIndex: number; slotIndex: number; visits: PatientVisit[] }>();

    patients.forEach((patient: string) => {
        const patientColumnTimes = columns.map((column: internalStage) => {
            const performed = patientVisits.find((visit: PatientVisit) =>
                NormalizePatientNumber(visit.numberInProtocol) === patient && MatchesStage(visit, column));

            return performed ? GetVisitTime(performed) : undefined;
        });

        const patientExtraVisits = extraVisits
            .filter((visit: PatientVisit) => NormalizePatientNumber(visit.numberInProtocol) === patient)
            .sort((a: PatientVisit, b: PatientVisit) =>
                (GetVisitTime(a) ?? Number.POSITIVE_INFINITY) - (GetVisitTime(b) ?? Number.POSITIVE_INFINITY));

        const usedSlotsByAnchor = new Map<number, number>();

        patientExtraVisits.forEach((extraVisit: PatientVisit) => {
            const time = GetVisitTime(extraVisit);
            let anchorIndex = time === undefined ? columns.length : -1;

            if (time !== undefined) {
                patientColumnTimes.forEach((columnTime: number | undefined, index: number) => {
                    if (columnTime !== undefined && columnTime <= time) {
                        anchorIndex = index;
                    }
                });
            }

            const slotIndex = usedSlotsByAnchor.get(anchorIndex) ?? 0;
            usedSlotsByAnchor.set(anchorIndex, slotIndex + 1);

            const slotKey = `${anchorIndex}-${slotIndex}`;
            const slot = slots.get(slotKey) || { anchorIndex, slotIndex, visits: [] };
            slot.visits.push(extraVisit);
            slots.set(slotKey, slot);
        });
    });

    const columnsByAnchor = new Map<number, number>();
    slots.forEach((slot) => {
        columnsByAnchor.set(slot.anchorIndex, Math.max(columnsByAnchor.get(slot.anchorIndex) ?? 0, slot.slotIndex + 1));
    });

    const firstOrder = columns.reduce(
        (min: number, column: internalStage) => Math.min(min, column.order),
        columns[0]?.order ?? 0
    );

    return Array.from(slots.values())
        .sort((a, b) => (a.anchorIndex - b.anchorIndex) || (a.slotIndex - b.slotIndex))
        .map((slot) => {
            const anchorOrder = slot.anchorIndex < 0
                ? firstOrder - 1
                : slot.anchorIndex >= columns.length
                    ? EXTRA_VISIT_ORDER_BASE
                    : columns[GetLastColumnIndexOfSameStage(columns, slot.anchorIndex)].order;

            const distinctTitles = new Set(
                slot.visits.map((visit: PatientVisit) => options.displayTitle(visit).toLowerCase()).filter(Boolean)
            );
            const fallbackTitle = options.fallbackTitle(slot.slotIndex, columnsByAnchor.get(slot.anchorIndex) ?? 1);

            return {
                columnKey: `${options.keyPrefix}-${slot.anchorIndex}-${slot.slotIndex}`,
                stageId: null,
                branchId: null,
                // patients can name the same slot differently; only a shared name titles the column
                stageName: distinctTitles.size === 1
                    ? (slot.visits.map(options.displayTitle).find(Boolean) || fallbackTitle)
                    : fallbackTitle,
                flowchartName: '',
                // the fraction keeps the column between its anchor and the next flowchart column
                order: anchorOrder + (slot.slotIndex + 1) / 1000 + options.orderOffset,
                crossVersionId: undefined,
                // modality varies per patient, so the column stays neutral and each popover shows the real one
                isRemote: false,
                remoteWarning: false,
                ...options.flags,
                extraVisitIds: new Set(slot.visits.map((visit: PatientVisit) => visit.id))
            };
        });
}

export const BuildExtraProtocolVisitColumns = (
    columns: internalStage[],
    patientVisits: PatientVisit[]
): internalStage[] =>
    BuildAdHocVisitColumns(columns, patientVisits, {
        matches: IsExtraProtocolPatientVisit,
        keyPrefix: 'extra',
        displayTitle: GetExtraVisitDisplayTitle,
        fallbackTitle: (slotIndex, slotsInAnchor) =>
            slotsInAnchor > 1 ? `Visita no programada ${slotIndex + 1}` : 'Visita no programada',
        flags: { isExtraProtocolVisit: true },
        orderOffset: 0,
    });

/**
 * Columnas de las anotaciones. Se ubican con la misma cronología por paciente
 * que las visitas extra, pero no son visitas: no cuentan como actividad ni
 * arrastran estado, sólo marcan que ese día hubo una nota.
 */
export const BuildNoteVisitColumns = (
    columns: internalStage[],
    patientVisits: PatientVisit[]
): internalStage[] =>
    BuildAdHocVisitColumns(columns, patientVisits, {
        matches: IsNoteVisit,
        keyPrefix: 'note',
        // El título técnico es "NOTE": no es un nombre para encabezar una columna.
        displayTitle: () => '',
        fallbackTitle: (slotIndex, slotsInAnchor) =>
            slotsInAnchor > 1 ? `Nota ${slotIndex + 1}` : 'Nota',
        flags: { isNoteVisit: true },
        // Medio milésimo: cae después de la visita extra del mismo slot y antes
        // de la siguiente, en vez de empatar con ella.
        orderOffset: 0.0005,
    });

export const GetRealStages = (stages: ContractStage[], visist: ProtocolVisit[], patientVisits: PatientVisit[] = []): internalStage[]  => {

    let realStages: internalStage[] = [];

    const protocolVisitsByTitle = new Map<string, ProtocolVisit>();
    visist.forEach((visit: ProtocolVisit) => {
        protocolVisitsByTitle.set(visit.title?.toLowerCase(), visit);
    });

    // convert stage-to-visit associations to internalStage columns
    let stagesAsInternalStage: internalStage[] = stages.sort(
        (a: ContractStage, b: ContractStage) => a.stageOrder - b.stageOrder
    ).flatMap((stage: ContractStage, index) => {
        const stageItems = stage.contractStageItems || [];

        if (stageItems.length > 0) {
            return stageItems.map((item, itemIndex) => {
                const visitByName = item.emrProtocolVisitName ? protocolVisitsByTitle.get(item.emrProtocolVisitName.toLowerCase()) : undefined;
                const crossVersionId = item.ermProtocolVisitId || visitByName?.crossVersionId;

                return {
                    columnKey: `stage-${stage.id}-item-${item.id ?? itemIndex}`,
                    stageId: stage.id,
                    branchId: stage.branchId,
                    stageName: stage.stageName,
                    flowchartName: item.emrProtocolVisitName || visitByName?.title || '',
                    order: index * 100 + itemIndex,
                    crossVersionId,
                    isRemote: !stage.isOnSiteMandatory,
                    remoteWarning: false
                };
            });
        }

        return [{
            columnKey: `stage-${stage.id}`,
            stageId: stage.id,
            branchId: stage.branchId,
            stageName: stage.stageName,
            flowchartName: '',
            order: index * 100,
            crossVersionId: stage.emrProtocolVisitIdRelated,
            isRemote: !stage.isOnSiteMandatory,
            remoteWarning: false
        }];
    });

    console.log(stagesAsInternalStage);

    // convert visits to internalStage
    let visitsAsInternalStage: internalStage[] = visist.sort(
        (a: ProtocolVisit, b: ProtocolVisit) => a.order - b.order
    ).map((visit: ProtocolVisit, index) => {
        return {columnKey: `visit-${visit.id ?? visit.crossVersionId}`, stageId: null, branchId: null, stageName: visit.title, flowchartName: visit.title, order: index * 100, crossVersionId: visit.crossVersionId, isRemote: visit.type !== 0, remoteWarning: false};
    });

    console.log(visitsAsInternalStage);

    realStages = [...stagesAsInternalStage];

    let FinishStages: internalStage[] = [];
    
    realStages.forEach((visit: internalStage) => {
        let foundStage = visitsAsInternalStage.find((stage: internalStage) => stage.crossVersionId === visit.crossVersionId);
        if(foundStage) {
            const currentVisit = {...visit, flowchartName: foundStage.stageName, crossVersionId: foundStage.crossVersionId, isRemote: visit.isRemote, remoteWarning: visit.isRemote && !foundStage.isRemote};
            FinishStages.push(currentVisit);
            //visitsAsInternalStage = visitsAsInternalStage.filter((stage: internalStage) => stage.crossVersionId !== visit.crossVersionId);
        } else {
            foundStage = visitsAsInternalStage.find((stage: internalStage) => CompareVisitsNames(visit.stageName, stage.stageName));
            if(foundStage) {
                const currentVisit = {...visit, flowchartName: foundStage.stageName, crossVersionId: foundStage.crossVersionId, isRemote: visit.isRemote, remoteWarning: visit.isRemote && !foundStage.isRemote};
                FinishStages.push(currentVisit);
                //visitsAsInternalStage = visitsAsInternalStage.filter((stage: internalStage) => stage.order !== visit.order);
            } else {
                FinishStages.push(visit); 
            }
        }
    });

    FinishStages = [...FinishStages, ...visitsAsInternalStage.filter((visit: internalStage) => !FinishStages.some((stage: internalStage) => stage.crossVersionId === visit.crossVersionId))];

    FinishStages = FinishStages.sort((a: internalStage, b: internalStage) => a.order - b.order);
    FinishStages = [
        ...FinishStages,
        ...BuildExtraProtocolVisitColumns(FinishStages, patientVisits),
        ...BuildNoteVisitColumns(FinishStages, patientVisits),
    ];

    return FinishStages.sort((a: internalStage, b: internalStage) => a.order - b.order);
}

// check if a visit is realized
export const IsVisitExist = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): boolean => {
    return visits.some((visit: PatientVisit) => IsSamePatient(visit, patientNumberInProtocol) && MatchesStage(visit, stage));
}

// return visit status
export const GetVisitStatus = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): string => {
    const visit = visits.find((visit: PatientVisit) =>
        IsSamePatient(visit, patientNumberInProtocol) && MatchesStage(visit, stage));
    if(visit !== undefined) {
        return GetVisitStatusByVisit(visit.status);
    } else {
        return '';
    }
}

// get visit status by visit
export const GetVisitStatusByVisit = (visitStatus: number): string => {
    switch(visitStatus) {
        case 0: return 'Started';
        case 1: return 'Completed';
        case 2: return 'Skipped';
        case 3: return 'Failed';
        case 4: return 'Changed';
        case 5: return 'AutoCompleted';
        default: return 'Not Realized';
    }
}

export const GetVisitStatusDescription = (status: number): string => {
    switch(status) {
        case 0: return 'Iniciada';
        case 1: return 'Completada';
        case 2: return 'Salteada';
        case 3: return 'Falla de selección';
        case 4: return 'Modificada';
        case 5: return 'Autocompletada';
        default: return 'No realizada';
    }
}

export const getVisit = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): PatientVisit | undefined => {
    return visits.find((visit: PatientVisit) => visit.numberInProtocol.toLowerCase() === patientNumberInProtocol.toLowerCase() && visit.order === stage.order);
}

// get visit by patient and stage
export const GetVisitByPatientAndStage = (visits: PatientVisit[], patientNumberInProtocol: string, stage: internalStage): PatientVisit | null => {
    return visits.find((visit: PatientVisit) => IsSamePatient(visit, patientNumberInProtocol) && MatchesStage(visit, stage)) || null;
}

// get first character of a string
export const GetFirstCharacter = (str: string): string => {
    return str.substring(0, 1).toLowerCase();
}

// get number of a string
export const GetNumber = (str: string): number => {
    return parseInt(str.replace(/\D/g, ''));
}

export const CompareVisitsNames = (stageName: string, visitName: string): boolean => {
    const stageInitial = GetFirstCharacter(stageName);
    const visitInitial = GetFirstCharacter(visitName);

    const stageNumber = GetNumber(stageName);
    const visitNumber = GetNumber(visitName);

    return stageInitial === visitInitial && stageNumber === visitNumber;
};

// styles
export const useStyles = makeStyles({
    contentHeader: {
      marginTop: "0",
    },
});